import React, { useEffect, useState } from "react";
import { Input, DatePicker, TimePicker, Select, Checkbox, Tooltip, Modal } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
    DynamicFieldConfig,
    FieldOption,
    FieldType,
    isBoolean,
    isDropdownType,
    isCheckboxType,
    isDateType,
    isTimeType,
    getVisibleFields,
    parseToIso,
    isInvalidDefaultValue,
    isProfileField,
    isEditableUserEntryField,
} from "./dynamicFieldUtils";

dayjs.extend(customParseFormat);

// ── Props ─────────────────────────────────────────────────────────────────────

interface DynamicFieldsSectionProps {
    dynamicConfigLoading: boolean;
    error: any;
    dynamicConfig: any;
    formData: any;
    handleInputChange: (e: { target: { name: string; value: any } }) => void;
    validationErrors: { [key: string]: string };
    userData?: any;
    isReviewMode?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const DynamicFieldsSection: React.FC<DynamicFieldsSectionProps> = ({
    dynamicConfigLoading,
    error,
    dynamicConfig,
    formData,
    handleInputChange,
    validationErrors,
    userData,
    isReviewMode = false,
}) => {
    const [showTermsModal, setShowTermsModal] = useState(false);
    // ── Extract fields array from however the config is nested ──────────────
    const rawFields: DynamicFieldConfig[] =
        dynamicConfig?.configData?.parsedDynamicFields ??
        dynamicConfig?.parsedDynamicFields ??
        dynamicConfig?.configData?.DynamicFields_parsed ??
        [];

    const termConditions: any[] =
        dynamicConfig?.configData?.parsedTermCondition ??
        dynamicConfig?.parsedTermCondition ??
        [];

    // ── Pre-fill SAP-provided DefaultValues and profile values on config change ─────
    useEffect(() => {
        if (!Array.isArray(rawFields) || rawFields.length === 0) return;

        const userPayScale =
            dynamicConfig?.employeeDetails?.pay_scale ||
            dynamicConfig?.pay_scale ||
            userData?.details?.pay_scale ||
            userData?.pay_scale ||
            "";

        const userSalary =
            dynamicConfig?.employeeDetails?.basicSalary ||
            dynamicConfig?.basicSalary ||
            userData?.details?.basicSalary ||
            userData?.basicSalary ||
            "";

        const empType = dynamicConfig?.employeeDetails?.employmentType ?? userData?.details?.employmentType ?? "";
        const empStatus = dynamicConfig?.employeeDetails?.empStatus ?? userData?.details?.empStatus ?? "";
        const userPhone = userData?.details?.phone || userData?.phone || "";

        rawFields.forEach((field) => {
            const key = field.HeaderCustomerField ?? "";
            if (!key) return;

            const labelLower = (field.Label ?? "").toLowerCase().trim();
            const readOnly = isBoolean(field.ReadOnlyForEmp);

            if (labelLower.includes("pay scale")) {
                const scale = userPayScale || field.DefaultValue || "";
                handleInputChange({ target: { name: key, value: scale } });
            } else if (labelLower.includes("basic salary")) {
                const salary = userSalary || field.DefaultValue || "";
                handleInputChange({ target: { name: key, value: salary } });
            } else if (labelLower.includes("recruitment") || labelLower.includes("employment status")) {
                const status = (empType || empStatus) ? `${empType} - ${empStatus}` : (field.DefaultValue || "");
                handleInputChange({ target: { name: key, value: status } });
            } else if ((labelLower === "mobile number" || labelLower === "phone") && readOnly && userPhone) {
                handleInputChange({ target: { name: key, value: userPhone } });
            } else if (readOnly && field.DefaultValue) {
                // Only prefill read-only system defaults that are NOT invalid copy-paste artifacts
                if (!isInvalidDefaultValue(field, field.DefaultValue, userPayScale, userSalary)) {
                    handleInputChange({ target: { name: key, value: field.DefaultValue } });
                }
            }
            // User-editable transaction fields (!readOnly) are NEVER pre-filled! They start clean/empty.
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawFields, dynamicConfig]);

    // ── Loading / error states ──────────────────────────────────────────────
    if (dynamicConfigLoading) {
        return (
            <div className="row mt-4">
                <div className="col-12">
                    <div className="d-flex align-items-center gap-2 text-muted p-3 bg-light rounded-3">
                        <span className="spinner-border spinner-border-sm" role="status" style={{ color: "#A93226" }} />
                        <span>Loading form configuration…</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="row mt-4">
                <div className="col-12">
                    <div className="alert alert-danger rounded-4 border-0 d-flex align-items-center p-3">
                        <i className="ti ti-alert-triangle fs-4 text-danger me-2" />
                        <div>
                            <strong>Configuration Error</strong>
                            <p className="mb-0 small">{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!Array.isArray(rawFields) || rawFields.length === 0) return null;

    // ── Filter & sort fields for creation form ──────────────────────────────
    const visibleFields = getVisibleFields(rawFields, "create");
    if (visibleFields.length === 0) return null;

    // ── Separate checkbox fields (rendered as a block) vs regular fields ────
    const checkboxFields = visibleFields.filter((f) => isCheckboxType(f.ViewOfFieldType as FieldType));
    const regularFields = visibleFields.filter((f) => !isCheckboxType(f.ViewOfFieldType as FieldType));

    // ── Field renderer ──────────────────────────────────────────────────────
    const renderField = (field: DynamicFieldConfig, idx: number) => {
        const key = field.HeaderCustomerField ?? "";
        let label = field.Label ?? key;

        if (label.endsWith("*")) {
            label = label.slice(0, -1).trim();
        }
        const labelLower = label.toLowerCase().trim();
        const keyLower = key.toLowerCase().trim();

        const isActualBillField =
            labelLower.includes("actual bill") ||
            labelLower.includes("bill amount") ||
            keyLower.includes("act_bill") ||
            keyLower.includes("actualbill");

        const type = field.ViewOfFieldType as FieldType;
        const opts: FieldOption[] = field.OptionsValue?.results ?? [];
        const mandatory = isBoolean(field.CustFldMandatory) || isActualBillField;
        const readOnly = isBoolean(field.ReadOnlyForEmp);
        const effectiveReadOnly = isReviewMode || readOnly;
        const hint = field.InformationText;

        const userPayScale =
            dynamicConfig?.employeeDetails?.pay_scale ||
            dynamicConfig?.pay_scale ||
            userData?.details?.pay_scale ||
            userData?.pay_scale ||
            "";

        const userSalary =
            dynamicConfig?.employeeDetails?.basicSalary ||
            dynamicConfig?.basicSalary ||
            userData?.details?.basicSalary ||
            userData?.basicSalary ||
            "";

        // Dynamic value resolution
        let currentValue = formData[key];

        if (labelLower.includes("pay scale")) {
            currentValue = userPayScale || field.DefaultValue || "";
        } else if (labelLower.includes("basic salary")) {
            currentValue = userSalary || field.DefaultValue || "";
        } else if (labelLower.includes("recruitment") || labelLower.includes("employment status")) {
            const empType = dynamicConfig?.employeeDetails?.employmentType ?? userData?.details?.employmentType ?? "";
            const empStatus = dynamicConfig?.employeeDetails?.empStatus ?? userData?.details?.empStatus ?? "";
            currentValue = ((empType || empStatus) ? `${empType} - ${empStatus}` : "") || field.DefaultValue || "";
        } else if (effectiveReadOnly) {
            // For read-only fields, resolve from formData or sanitized DefaultValue
            if (currentValue === undefined || currentValue === null || currentValue === "") {
                const rawDef = field.DefaultValue ?? "";
                currentValue = isInvalidDefaultValue(field, rawDef, userPayScale, userSalary) ? "" : rawDef;
            }
        } else {
            // User-editable transaction field: value MUST strictly come from user entry (formData[key]).
            // If untouched or if it contains an invalid leaked pay scale/salary, it MUST be empty!
            // CRITICAL: NEVER fall back to field.DefaultValue!
            if (currentValue === undefined || currentValue === null || isInvalidDefaultValue(field, currentValue, userPayScale, userSalary)) {
                currentValue = "";
            }
        }

        const onChange = (val: any) => handleInputChange({ target: { name: key, value: val } });
        const errMsg = validationErrors[key];

        // ── Checkbox ───────────────────────────────────────────────────────
        if (isCheckboxType(type)) {
            const checked = currentValue === "YES" || currentValue === true || String(currentValue).toLowerCase() === "true" || currentValue === "X";
            return (
                <div key={`chk-${idx}`} className="col-12 col-sm-6 my-1" id={`field-${key}`}>
                    <Checkbox
                        checked={checked}
                        onChange={(e) => onChange(e.target.checked ? "YES" : "")}
                        disabled={isReviewMode}
                    >
                        <span className="fw-medium text-dark small">
                            {label}
                            {mandatory && <span className="text-danger ms-1">*</span>}
                        </span>
                    </Checkbox>
                    {errMsg && <div className="text-danger" style={{ fontSize: 11 }}>{errMsg}</div>}
                </div>
            );
        }

        // ── Time field ────────────────────────────────────────────────────
        if (isTimeType(type, key, label)) {
            const timeVal = currentValue ? dayjs(currentValue, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null;
            return (
                <div key={`fld-${idx}`} className="col-md-4 mb-3" id={`field-${key}`}>
                    <FieldLabel label={label} mandatory={mandatory} hint={hint} />
                    <TimePicker
                        className="w-100"
                        style={{ height: 40, borderRadius: 8, fontWeight: "600", color: "#000" }}
                        format="HH:mm"
                        placement="bottomLeft"
                        popupStyle={{ zIndex: 9999 }}
                        value={timeVal?.isValid() ? timeVal : null}
                        onChange={(time) => onChange(time ? time.format("HH:mm:ss") : "")}
                        disabled={effectiveReadOnly}
                        placeholder={`Select ${label}`}
                    />
                    {errMsg && <div className="text-danger small mt-1">{errMsg}</div>}
                </div>
            );
        }

        // ── Date field ────────────────────────────────────────────────────
        if (isDateType(type, key, label)) {
            const iso = parseToIso(currentValue);
            const dayjsVal = iso ? dayjs(iso, "YYYY-MM-DD") : null;
            return (
                <div key={`fld-${idx}`} className="col-md-4 mb-3" id={`field-${key}`}>
                    <FieldLabel label={label} mandatory={mandatory} hint={hint} />
                    <DatePicker
                        className="w-100"
                        style={{ height: 40, borderRadius: 8, fontWeight: "600", color: "#000" }}
                        format="DD-MM-YYYY"
                        placement="bottomLeft"
                        popupStyle={{ zIndex: 9999 }}
                        value={dayjsVal?.isValid() ? dayjsVal : null}
                        onChange={(date) => {
                            if (date) {
                                const ms = Date.UTC(date.year(), date.month(), date.date());
                                onChange(`/Date(${ms})/`);
                            } else {
                                onChange("");
                            }
                        }}
                        disabled={effectiveReadOnly}
                        placeholder={`Select ${label}`}
                    />
                    {errMsg && <div className="text-danger small mt-1">{errMsg}</div>}
                </div>
            );
        }

        // ── Dropdown (E / G / D / F) ──────────────────────────────────────
        if (isDropdownType(type)) {
            const selectOpts = opts.map((o) => ({
                value: o.SelectedValue,
                label: o.SelectedText ?? o.SelectedValue,
            }));
            return (
                <div key={`fld-${idx}`} className="col-md-4 mb-3" id={`field-${key}`}>
                    <FieldLabel label={label} mandatory={mandatory} hint={hint} />
                    <Select
                        showSearch
                        allowClear
                        className="w-100"
                        style={{ height: 40, fontWeight: "600", color: "#000" }}
                        placeholder={`Select ${label}`}
                        value={currentValue || undefined}
                        onChange={onChange}
                        disabled={effectiveReadOnly}
                        options={selectOpts}
                        optionFilterProp="label"
                    />
                    {errMsg && <div className="text-danger small mt-1">{errMsg}</div>}
                </div>
            );
        }

        // ── Text / Summation / Line No ────────────────────────────────────
        const widthOnESSScreen = parseInt(field.WidthOnESSScreen || "0", 10);
        const isAssetField = labelLower.includes("asset") || keyLower.includes("asset");
        const isBillNoField = labelLower.includes("bill no") || labelLower.includes("bill number") || keyLower.includes("bill_no") || keyLower.includes("billno") || (labelLower.includes("bill") && !labelLower.includes("amount"));
        const maxLength = widthOnESSScreen > 0 ? widthOnESSScreen : (isAssetField ? 16 : undefined);

        return (
            <div key={`fld-${idx}`} className="col-md-4 mb-3" id={`field-${key}`}>
                <FieldLabel label={label} mandatory={mandatory} hint={hint} />
                {type === "B" ? (
                    <Input.TextArea
                        rows={2}
                        placeholder={effectiveReadOnly ? "" : `Enter ${label}`}
                        value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ""}
                        onChange={(e) => {
                            let val = e.target.value;
                            if (maxLength && val.length > maxLength) {
                                val = val.substring(0, maxLength);
                            }
                            onChange(val);
                        }}
                        disabled={effectiveReadOnly}
                        maxLength={maxLength}
                        autoComplete="off"
                        style={{ borderRadius: 8, fontWeight: "600", color: "#000" }}
                    />
                ) : (
                    <Input
                        placeholder={effectiveReadOnly ? "" : `Enter ${label}`}
                        style={{ height: 40, borderRadius: 8, fontWeight: "600", color: "#000" }}
                        value={currentValue !== undefined && currentValue !== null ? String(currentValue) : ""}
                        onChange={(e) => {
                            let val = e.target.value;
                            if (labelLower.includes("mobile") || labelLower.includes("phone") || labelLower.includes("moblie")) {
                                val = val.replace(/\D/g, ""); // Restrict to numbers only
                                if (val.length > 10) val = val.substring(0, 10); // Restrict to 10 digits
                            } else if (labelLower.includes("pan")) {
                                val = val.toUpperCase().replace(/[^A-Z0-9]/g, ""); // Uppercase alphanumeric only
                                if (val.length > 10) val = val.substring(0, 10); // Restrict to 10 characters
                            } else if (isActualBillField || key.startsWith("AMT") || key.startsWith("RQAMT") || labelLower.includes("cost") || (labelLower.includes("amount") && !labelLower.includes("number") && !labelLower.includes("no") && !labelLower.includes("num"))) {
                                if (val !== "" && !/^\d*\.?\d*$/.test(val)) {
                                    return;
                                }
                            } else if (isAssetField) {
                                const max = maxLength || 16;
                                if (val.length > max) val = val.substring(0, max);
                            }
                            if (maxLength && val.length > maxLength) {
                                val = val.substring(0, maxLength);
                            }
                            onChange(val);
                        }}
                        disabled={effectiveReadOnly}
                        readOnly={effectiveReadOnly}
                        maxLength={maxLength}
                        autoComplete="off"
                    />
                )}
                {errMsg && <div className="text-danger small mt-1">{errMsg}</div>}
            </div>
        );
    };

    // ── Terms helper ────────────────────────────────────────────────────────
    const renderTerms = () => {
        if (!termConditions.length) return null;
        // Concatenate consecutive lines with same TagColumn into paragraphs
        const paragraphs: string[] = [];
        let current = "";
        termConditions.forEach((tc: any) => {
            const line = (tc.TextLine ?? "").trim();
            if (line === "") {
                if (current.trim()) {
                    paragraphs.push(current.trim());
                    current = "";
                }
            } else {
                current += (current ? " " : "") + line;
            }
        });
        if (current.trim()) paragraphs.push(current.trim());

        return paragraphs.filter((p) => p.length > 0).map((p, i) => (
            <li key={i} className="mb-1 text-secondary small">{p}</li>
        ));
    };

    return (
        <div className="row mt-2">
            <div className="col-12">
                {/* ── Regular Fields Card ───────────────────────────────── */}
                <div className="p-3 bg-white rounded-4 border border-light shadow-xs mb-3">
                    <h5 className="mb-3 fw-bold d-flex align-items-center" style={{ color: "#A93226", fontSize: 15 }}>
                        <i className="ti ti-info-circle me-2" />
                        Claim Details &amp; Information
                    </h5>
                    <div className="row gx-3 gy-2">
                        {regularFields.map((field, idx) => renderField(field, idx))}
                    </div>

                    {/* ── Checkbox block (declaration-style) ─────────────── */}
                    {checkboxFields.length > 0 && (
                        <div className="mt-3 pt-3 border-top">
                            <div className="fw-semibold small text-muted mb-2 text-uppercase" style={{ letterSpacing: 1 }}>
                                Declarations
                            </div>
                            <div className="row gx-3 gy-1">
                                {checkboxFields.map((field, idx) => renderField(field, idx))}
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Terms & Conditions ───────────────────────────────── */}
                {termConditions.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-3 mb-3" id="field-acceptTerms">
                        <div className="card-body p-3">
                            <Checkbox
                                checked={formData.acceptTerms || false}
                                disabled={isReviewMode}
                                onChange={(e) => handleInputChange({ target: { name: "acceptTerms", value: e.target.checked } })}
                            >
                                <span className="text-dark fw-medium" style={{ fontSize: 13 }}>
                                    I acknowledge and accept the{" "}
                                    <span
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setShowTermsModal(true);
                                        }}
                                        style={{ color: "#A93226", textDecoration: "underline", cursor: "pointer" }}
                                    >
                                        Terms &amp; Conditions
                                    </span>
                                    <span className="text-danger ms-1">*</span>
                                </span>
                            </Checkbox>
                            {validationErrors?.acceptTerms && (
                                <div className="text-danger small mt-1">{validationErrors.acceptTerms}</div>
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/* ── Terms & Conditions Modal ────────────────────────────── */}
            <Modal
                title={
                    <span className="fw-bold text-dark fs-16">
                        <i className="ti ti-file-description me-2 text-danger"></i>
                        Terms &amp; Conditions
                    </span>
                }
                open={showTermsModal}
                onCancel={() => setShowTermsModal(false)}
                footer={[
                    <button
                        key="close"
                        className="btn btn-outline-secondary btn-sm px-4 rounded-pill"
                        onClick={() => setShowTermsModal(false)}
                    >
                        Close
                    </button>
                ]}
                centered
                zIndex={9999}
            >
                <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "10px" }}>
                    <ul className="mb-0 ps-3" style={{ lineHeight: 1.7 }}>
                        {renderTerms()}
                    </ul>
                </div>
            </Modal>
        </div>
    );
};

// ── Sub-component: Field Label ────────────────────────────────────────────────

const FieldLabel: React.FC<{ label: string; mandatory: boolean; hint?: string }> = ({ label, mandatory, hint }) => (
    <label className="form-label text-dark fw-bold mb-1" style={{ fontSize: 13 }}>
        {label}
        {mandatory && <span className="text-danger ms-1">*</span>}
        {hint && (
            <Tooltip title={hint}>
                <i className="ti ti-info-circle text-muted ms-1" style={{ fontSize: 12 }} />
            </Tooltip>
        )}
    </label>
);
